import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import {
  FundResponse,
  FundRecommendationResponse,
  useRecommendApiV1UsersUserIdSingleProductRecommendationsPost,
} from "@rentablo/api/recommender";
import { FilterValue } from "@components/filters/types";
import { useMyself } from "@api";
import { useIsinNames } from "./useIsinNames";
import {
  filterRecommendationsByType,
  getVisibleRecommendations,
  getUniqueTypes,
} from "./utils";

export const useRecommendations = () => {
  const { data: userData } = useMyself();
  const { mutate: getRecommendations, isPending } =
    useRecommendApiV1UsersUserIdSingleProductRecommendationsPost();
  const [showMore, setShowMore] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<FilterValue[]>(["all"]);
  const [recommendations, setRecommendations] =
    useState<FundRecommendationResponse[]>();
  const [originalFund, setOriginalFund] = useState<FundResponse>();
  const { isin } = useParams<{ isin: string }>();

  const recommendationsWithNames = useIsinNames(recommendations);
  const filteredRecommendations = useMemo(
    () => filterRecommendationsByType(recommendationsWithNames, selectedTypes),
    [recommendationsWithNames, selectedTypes]
  );

  const visibleRecommendations = useMemo(
    () => getVisibleRecommendations(filteredRecommendations, showMore),
    [filteredRecommendations, showMore]
  );

  const uniqueTypesCount = useMemo(
    () => getUniqueTypes(recommendationsWithNames),
    [recommendationsWithNames]
  );

  useEffect(() => {
    if (!isin || !userData?.id) return;

    getRecommendations(
      {
        userId: userData.id,
        recommendationRequest: { isin, n_recommendations: 25 },
      },
      {
        onSuccess: (data) => {
          setRecommendations(data.recommendations ?? []);
          setOriginalFund(data.original_fund);
        },
        onError: () => setRecommendations([]),
      }
    );
  }, [isin, getRecommendations, userData?.id]);

  return {
    recommendations: recommendationsWithNames,
    originalFund,
    showLoader: isPending || recommendations === undefined,
    visibleRecommendations,
    uniqueTypesCount,
    selectedTypes,
    setSelectedTypes,
    showMore,
    setShowMore,
  };
};
